import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { TeamMember, UserRole } from "@/types/team";
import { MemberActionsMenu } from "./MemberActionsMenu";
import { useAuth } from "@/hooks/useAuth";
import { canManageTeam } from "@/lib/permissions";

interface MembersTableProps {
  members: TeamMember[];
  isLoading?: boolean;
}

function RoleBadge({ role }: { role: UserRole }) {
  if (role === "owner") {
    return (
      <Badge variant="default" className="capitalize">
        {role}
      </Badge>
    );
  }
  if (role === "admin") {
    return (
      <Badge variant="secondary" className="capitalize">
        {role}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="capitalize text-muted-foreground">
      {role}
    </Badge>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-muted p-3 mb-4">
        <svg
          className="h-6 w-6 text-muted-foreground"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </div>
      <h3 className="font-semibold mb-1">No team members yet</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        Your team will appear here once members accept their invitations.
      </p>
    </div>
  );
}

function LoadingSkeleton({
  hasManagePermission,
}: {
  hasManagePermission: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px]">Email</TableHead>
            <TableHead className="min-w-[100px]">Role</TableHead>
            <TableHead className="min-w-[120px] hidden sm:table-cell">
              Joined
            </TableHead>
            {hasManagePermission && (
              <TableHead className="min-w-[100px]">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-[180px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-[60px] rounded-full" />
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <Skeleton className="h-4 w-[100px]" />
              </TableCell>
              {hasManagePermission && (
                <TableCell>
                  <Skeleton className="h-8 w-8" />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function MembersTable({ members, isLoading }: MembersTableProps) {
  const { role } = useAuth();
  const hasManagePermission = canManageTeam(role);

  if (isLoading) {
    return <LoadingSkeleton hasManagePermission={hasManagePermission} />;
  }

  if (!members || members.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px]">Email</TableHead>
            <TableHead className="min-w-[100px]">Role</TableHead>
            <TableHead className="min-w-[120px] hidden sm:table-cell">
              Joined
            </TableHead>
            {hasManagePermission && (
              <TableHead className="min-w-[100px]">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="font-mono text-sm">
                {member.email}
              </TableCell>
              <TableCell>
                <RoleBadge role={member.role} />
              </TableCell>
              <TableCell className="text-muted-foreground hidden sm:table-cell font-mono text-sm">
                {member.joined_at
                  ? new Date(member.joined_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "Pending"}
              </TableCell>
              {hasManagePermission && (
                <TableCell>
                  <MemberActionsMenu member={member} />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
